import { Injectable } from '@angular/core';
import { Observable, retry } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class Api {
  apiUrl = environment.apiUrl;
  klApiUrl = environment.klApiUrl;
  retryCount = 1;

  private refreshSubject = new Subject<void>();
  /** Subscribe to this in the IssuesTable component */
  refresh$ = this.refreshSubject.asObservable();
  showChatHistory$ = new BehaviorSubject<boolean>(false);
  chatHistoryIssueIds$ = new BehaviorSubject<string>('');
  showAuditLog$ = new BehaviorSubject<boolean>(false);
  auditLogIssueIds$ = new BehaviorSubject<string>('');
  auditLogPflmId$ = new BehaviorSubject<number>(0);
  updatedConversationIds$ = new BehaviorSubject<string[]>([]);

  supervisorUpdateLogContext$ = new BehaviorSubject<{ issueId: number | null, pflmId: number | null }>({
    issueId: null,
    pflmId: null
  });


  private notesSubject = new BehaviorSubject<{ open: boolean; data: any }>({
    open: false,
    data: null,
  });

  notesState$ = this.notesSubject.asObservable();

  constructor(private http: HttpClient) {
    this.apiUrl = environment.apiUrl;
  }

  /**
   * Checks if a given value is numeric (either a number type or a string representing a number).
   * @param value The value to check.
   * @returns True if the value is numeric, false otherwise.
   */
  isNumeric(value: any): boolean {
    if (typeof value === 'number') {
      return isFinite(value);
    }
    if (typeof value === 'string' && value.trim() !== '') {
      // Number() is stricter than parseFloat() as it won't parse "123abc"
      const num = Number(value);
      return !isNaN(num) && isFinite(num);
    }
    return false;
  }


  /** Call this from Notes after submit success */
  triggerRefresh(): void {
    this.refreshSubject.next();
    this.notesSubject.next({ open: false, data: null }); // Close notes modal after refresh
  }

  GetFLMListBySupervisor(payload: any) {
    let methodName = 'genai/supervisor/flms';
    const url = `${this.apiUrl}${methodName}?AssociateId=${payload.AssociateId}`;
    return this.http.get(url);
  }

  GetBUListByRiskOfficer(data: any) {
    let methodName = 'genai/issues/bu-role';
    return this.http.post<any>(`${this.apiUrl}${methodName}`, data);
  }

  GetMasterDataForDropdown() {
    let methodName = 'genai/master-data/risk-filters';
    const url = `${this.apiUrl}${methodName}`;
    return this.http.get(url);
  }

  getNotesDataAPI(data: any, reviewerID: string): Observable<any> {
    // const headers = new HttpHeaders({
    //   'X-Associate-Id': reviewerID   // <-- custom header
    // });
    // return this.http.post<any>(`${this.apiUrl}${methodName}`, data, { headers });
    let methodName = 'genai/issues/search';
    return this.http.post<any>(`${this.apiUrl}${methodName}`, data);
  }

  saveRisksAPI(data: any): Observable<any> {
    let methodName = 'genai/risk/update-list';
    return this.http.post<any>(`${this.apiUrl}${methodName}`, data);
  }

  supervisorSubmitsAPI(data: any): Observable<any> {
    let methodName = 'genai/risk/update-pending-user';
    return this.http.post<any>(`${this.apiUrl}${methodName}`, data);
  }

  fetchChatHistoryAPI(data: any): Observable<any> {
    let methodName = 'genai/conversations/get';
    return this.http.post<any>(`${this.apiUrl}${methodName}`, data);
  }

  fetchAuditLogsAPI(data: any): Observable<any> {
    let methodName = 'genai/audit-logs';
    return this.http.post<any>(`${this.apiUrl}${methodName}`, data);
  }

  GenAIInsertIntoRiskRegister(data: any): Observable<any> {
    let methodName = 'dhd/v1/risk/sync-register';
    return this.http.post<any>(`${this.apiUrl}${methodName}`, data);
  }

  GenAIUpdateIssueStatusRetention(data: any): Observable<any> {
    let methodName = 'genai/issues/update-retention';
    return this.http.post<any>(`${this.apiUrl}${methodName}`, data);
  }

  openNotes(data: any) {
    this.notesSubject.next({ open: true, data: data });
  }

  getAuthConfig(): Observable<any> {
    let methodName = 'auth/config';
    return this.http.get<any>(`${this.apiUrl}${methodName}`,).pipe(
      retry(this.retryCount)
    );
  }
  getAccessToken(payloadData: any): Observable<any> {
    let methodName = 'auth/google';
    console.log('Fetching access token with payload:', payloadData);
    return this.http.post<any>(`${this.apiUrl}${methodName}`, payloadData);
  }
  getAccessDetails(payloadData: any): Observable<any> {
    let methodName = `auth/bypass`;
    console.log('Fetching access details with payload:', payloadData);
    return this.http.post<any>(`${this.apiUrl}${methodName}`, payloadData);
  }
  getHolidayList(associate_id: any): Observable<any> {
    let methodName = `genai/holidays/by-associate`;
    console.log('Fetching holiday list with payload:', associate_id);
    return this.http.post<any>(`${this.apiUrl}${methodName}?AssociateId=${associate_id}`, { AssociateId: associate_id });
  }
  getProjectsForUser(associateId: string): Observable<any> {
    let methodName = `genai/associate/projects_process_details`;
    console.log('Fetching projects for user with associate ID:', associateId);
    return this.http.get<any>(`${this.apiUrl}${methodName}?AssociateId=${associateId}`);
  }
  getDailyCheckins(associate_id: any): Observable<any> {
    let methodName = `genai/checkin/daily-status`;
    console.log('Fetching daily checkins with payload:', associate_id);
    return this.http.post<any>(`${this.apiUrl}${methodName}?associate_id=${associate_id}`, associate_id);
  }
  getCheckinHistory(associate_id: any): Observable<any> {
    let methodName = `genai/checkin/history`;
    console.log('Fetching check-in history with payload:', associate_id);
    return this.http.post<any>(`${this.apiUrl}${methodName}?associate_id=${associate_id}`, associate_id);
  }
  getTotalIssues(pflm_id: any): Observable<any> {
    let methodName = `genai/issues/total`;
    console.log('Fetching total issues with payload:', pflm_id);
    return this.http.post<any>(`${this.apiUrl}${methodName}?pflm_id=${pflm_id}`, { pflm_id });
  }
  getHolidays(pflm_id: any): Observable<any> {
    let methodName = `genai/holidays`;
    console.log('Fetching holidays with payload:', pflm_id);
    return this.http.get<any>(`${this.apiUrl}${methodName}?ProcessFLMLocMapId=${pflm_id}`);
  }
  getInputOptions(): Observable<any> {
    let methodName = `genai/master-data/risk-filters`;
    console.log('Fetching configuration with payload:');
    return this.http.get<any>(`${this.apiUrl}${methodName}`).pipe(
      retry(this.retryCount)
    );
  }
  getSummarization(payloadData: any): Observable<any> {
    let methodName = `genai/issues/by-update-conversation`;
    console.log('Fetching summarization with payload:', payloadData);
    return this.http.post<any>(`${this.apiUrl}${methodName}`, payloadData);
  }
  updateSummarization(payloadData: any): Observable<any> {
    let methodName = `genai/issues/update-conversation-summary`;
    console.log('Updating summarization with payload:', payloadData);
    return this.http.post<any>(`${this.apiUrl}${methodName}`, payloadData);
  }

  updateFTECount(payloadData: any): Observable<any> {
    let methodName = `genai/associate/update-fte-count`;
    console.log('Updating FTE count with payload:', payloadData);
    return this.http.post<any>(`${this.apiUrl}${methodName}`, payloadData);
  }

  connectChat(payload: any, request_id: string = ''): Observable<any> {
    let methodName = `chat`;
    console.log('Connecting to chat with payload:', payload);
    const headers = new HttpHeaders({
      'X-Session-Id': request_id
    });
    return this.http.post<any>(`${this.klApiUrl}${methodName}`, payload, { headers });
  }

  startSession(payload: any, request_id: string = ''): Observable<any> {
    let methodName = `start-session`;
    console.log('Starting session with payload:', payload);
    const headers = new HttpHeaders({
      'X-Session-Id': request_id
    });
    return this.http.post<any>(`${this.klApiUrl}${methodName}`, payload, { headers });
  }

  getChatHistory(pflmId: any, associateId: string): Observable<any> {
    let methodName = `history`;
    console.log('Fetching chat history with payload:', pflmId);
    return this.http.get<any>(`${this.klApiUrl}${methodName}?process_flm_loc_map_id=${pflmId}&flm_id=${associateId}&hours=24`);
  }
 
}
